import { Box, Card, CardContent, CircularProgress, Typography } from "@mui/material";
import {
  getWeatherCodeEmoji,
  getWeatherCodeLabel,
  useOpenMeteoWeather,
} from "@/lib/weather";
import { formatTemp, formatWindSpeed } from "@/lib/units";
import { getDirectionLabel } from "@/lib/buoys";
import type { UnitSystem } from "@/atoms/units";
import { format, parseISO } from "date-fns";

interface WeatherCardProps {
  lat: number;
  lon: number;
  label: string;
  units: UnitSystem;
}

export default function WeatherCard({ lat, lon, label, units }: WeatherCardProps) {
  const { data: weather, isLoading, isError } = useOpenMeteoWeather(lat, lon);

  return (
    <Card
      elevation={2}
      sx={{
        borderRadius: 2,
        height: "100%",
      }}
    >
      <CardContent>
        <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>
          Weather — {label}
        </Typography>

        {isLoading && (
          <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
            <CircularProgress size={24} />
          </Box>
        )}

        {isError && (
          <Typography variant="body2" color="error">
            Unable to load weather data
          </Typography>
        )}

        {weather && (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
            {weather.daily.map((day, idx) => (
              <Box
                key={idx}
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  py: 1,
                  px: 1.5,
                  borderRadius: 1,
                  bgcolor: "action.hover",
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                  <Typography sx={{ fontSize: "1.4rem" }}>
                    {getWeatherCodeEmoji(day.weatherCode)}
                  </Typography>
                  <Box>
                    <Typography variant="body1" sx={{ fontWeight: 600 }}>
                      {dailyDateLabel(day.date)}
                    </Typography>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ fontSize: "0.8rem" }}
                    >
                      {getWeatherCodeLabel(day.weatherCode)}
                    </Typography>
                  </Box>
                </Box>
                <Box sx={{ textAlign: "right" }}>
                  <Typography variant="body1" sx={{ fontWeight: 700 }}>
                    {formatTemp(day.tempMax, units)} / {formatTemp(day.tempMin, units)}
                  </Typography>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ fontSize: "0.8rem" }}
                  >
                    Wind: {formatWindSpeed(day.windSpeedMax, units)}{" "}
                    {getDirectionLabel(day.windDirectionDominant)}
                  </Typography>
                </Box>
              </Box>
            ))}
          </Box>
        )}
      </CardContent>
    </Card>
  );
}

/**
 * Only label "Today" when the date actually matches today in the
 * user's local timezone. See the note in CompactForecast.tsx — Open-Meteo
 * returns days in the queried spot's local tz, which can disagree with
 * the viewer's local day.
 */
function dailyDateLabel(dateStr: string): string {
  const day = parseISO(dateStr);
  const now = new Date();
  const sameDay =
    day.getFullYear() === now.getFullYear() &&
    day.getMonth() === now.getMonth() &&
    day.getDate() === now.getDate();
  return sameDay ? "Today" : format(day, "EEE, MMM d");
}
